'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import styled from 'styled-components';
import { COLORS } from '@/constants/colors';

interface CarouselSlide {
  id: number;
  imageSrc: string;
  mobileImageSrc?: string;
  imageAlt: string;
  objectFit?: 'cover' | 'contain';
  link?: string;
  textOverlay?: {
    headline?: string;
    subheadline?: string;
    description?: string;
    position?: 'left' | 'center' | 'right';
  };
}

interface LightweightCarouselProps {
  slides: CarouselSlide[];
  autoPlaySpeed?: number;
}

/**
 * Lightweight CSS-only carousel - replaces react-multi-carousel
 * Reduces bundle size by ~1.8MB
 */
const LightweightCarousel: React.FC<LightweightCarouselProps> = ({
  slides,
  autoPlaySpeed = 5000,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const currentSlideRef = useRef(0);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Hidden slides never lazy-load, so after mount (the first slide is already
  // preloaded by then) the rest are switched to eager loading and fetched in the background.
  useEffect(() => {
    slideRefs.current.forEach((slide) => {
      slide?.querySelectorAll<HTMLImageElement>('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    });
  }, []);

  // A slide is ready once the image shown at this breakpoint has decoded pixels.
  // Checked on the DOM rather than via onLoad, which can fire before hydration.
  const isSlideReady = useCallback((index: number) => {
    const images = slideRefs.current[index]?.querySelectorAll('img') ?? [];
    return Array.from(images).some(
      (img) => img.offsetParent !== null && img.complete && img.naturalWidth > 0,
    );
  }, []);

  // Bumped on every manual navigation so autoplay restarts its full interval
  // instead of jumping again right after the user picked a slide.
  const [autoPlayKey, setAutoPlayKey] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      const current = currentSlideRef.current;
      const next = (current + 1) % slides.length;
      // Wait for the next banner to load so the crossfade never shows a blank frame.
      if (!isSlideReady(next)) return;
      currentSlideRef.current = next;
      setCurrentSlide(next);
    }, autoPlaySpeed);

    return () => clearInterval(timer);
  }, [slides.length, autoPlaySpeed, isSlideReady, autoPlayKey]);

  const goToSlide = useCallback((step: number) => {
    const next = (currentSlideRef.current + step + slides.length) % slides.length;
    currentSlideRef.current = next;
    setCurrentSlide(next);
    setAutoPlayKey((key) => key + 1);
  }, [slides.length]);

  const hasMultipleSlides = slides.length > 1;

  return (
    <CarouselWrapper
      aria-roledescription="carousel"
      onKeyDown={(e) => {
        if (!hasMultipleSlides) return;
        if (e.key === 'ArrowLeft') goToSlide(-1);
        if (e.key === 'ArrowRight') goToSlide(1);
      }}
    >
      {hasMultipleSlides && (
        <>
          <ArrowButton
            type="button"
            $side="left"
            aria-label="Previous slide"
            onClick={() => goToSlide(-1)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </ArrowButton>
          <ArrowButton
            type="button"
            $side="right"
            aria-label="Next slide"
            onClick={() => goToSlide(1)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </ArrowButton>
        </>
      )}
      <SlidesContainer>
        {slides.map((slide, index) => {
          const slideContent = (
            <SlideInner $hasLink={!!slide.link} $hasMobile={!!slide.mobileImageSrc}>
              {slide.mobileImageSrc && (
                <MobileImage
                  src={slide.mobileImageSrc}
                  alt={slide.imageAlt}
                  fill
                  priority={index === 0}
                  quality={85}
                  sizes="(max-width: 767px) 100vw, 1px"
                />
              )}
              <StyledImage
                $hasMobile={!!slide.mobileImageSrc}
                src={slide.imageSrc}
                alt={slide.imageAlt}
                fill
                priority={index === 0}
                quality={85}
                sizes={slide.mobileImageSrc ? '(max-width: 767px) 1px, 100vw' : '100vw'}
                $objectFit={slide.objectFit || 'cover'}
              />

              {slide.textOverlay && (
                <TextOverlay $position={slide.textOverlay.position || 'center'}>
                  <TextContent>
                    {slide.textOverlay.headline && (
                      <Headline>{slide.textOverlay.headline}</Headline>
                    )}
                    {slide.textOverlay.subheadline && (
                      <Subheadline>{slide.textOverlay.subheadline}</Subheadline>
                    )}
                    {slide.textOverlay.description && (
                      <Description>{slide.textOverlay.description}</Description>
                    )}
                  </TextContent>
                </TextOverlay>
              )}
            </SlideInner>
          );

          return (
            <Slide
              key={slide.id}
              $isActive={index === currentSlide}
              aria-hidden={index !== currentSlide}
              ref={(el) => {
                slideRefs.current[index] = el;
              }}
            >
              {slide.link ? (
                <SlideLink href={slide.link}>
                  {slideContent}
                </SlideLink>
              ) : (
                slideContent
              )}
            </Slide>
          );
        })}
      </SlidesContainer>
    </CarouselWrapper>
  );
};

export default LightweightCarousel;

// Styled Components
const CarouselWrapper = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
  background: ${COLORS.gray900};
`;

/* Frosted round arrows that sit quietly over the artwork and come forward on
   hover. On touch screens there is no hover, so they stay visible but smaller. */
const ArrowButton = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${props => props.$side}: 24px;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  color: ${COLORS.white};
  cursor: pointer;
  opacity: 0.75;
  transform: translateY(-50%);
  transition: opacity 0.25s ease, background-color 0.25s ease,
    border-color 0.25s ease, transform 0.25s ease;

  svg {
    width: 22px;
    height: 22px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2.5;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  ${CarouselWrapper}:hover & {
    opacity: 1;
  }

  /* Guarded so a tap on mobile doesn't leave the button stuck green. */
  @media (hover: hover) {
    &:hover {
      background: ${COLORS.brandGreen};
      border-color: ${COLORS.brandGreen};
      transform: translateY(-50%) scale(1.06);
    }
  }

  &:active {
    transform: translateY(-50%) scale(0.96);
  }

  &:focus-visible {
    opacity: 1;
    outline: 2px solid ${COLORS.white};
    outline-offset: 3px;
  }

  @media (max-width: 767px) {
    ${props => props.$side}: 10px;
    width: 34px;
    height: 34px;
    opacity: 0.85;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

/* All slides share one grid cell so they stack, and the carousel crossfades
   instead of sliding. */
const SlidesContainer = styled.div`
  display: grid;
`;

/* The outgoing slide fades out while the incoming one fades in, over the
   wrapper's dark background so the handover never flashes white. */
const Slide = styled.div<{ $isActive: boolean }>`
  grid-area: 1 / 1;
  position: relative;
  z-index: ${props => (props.$isActive ? 1 : 0)};
  opacity: ${props => (props.$isActive ? 1 : 0)};
  transition: opacity 1s ease-in-out;
  pointer-events: ${props => (props.$isActive ? 'auto' : 'none')};
`;

const SlideLink = styled(Link)`
  display: block;
  text-decoration: none;
  color: inherit;
`;

const SlideInner = styled.div<{ $hasLink?: boolean; $hasMobile?: boolean }>`
  cursor: ${props => props.$hasLink ? 'pointer' : 'default'};
  position: relative;
  width: 100%;
  /* Desktop banners are 2155x730: keep the full artwork (baked-in text and
     button) visible by sizing to the image ratio instead of a fixed height. */
  aspect-ratio: 2155 / 730;

  /* Mobile uses dedicated portrait artwork (headline, subcopy and button
     baked in) sized to match it almost exactly, so the whole image shows
     with no cropping. A slide without mobile artwork keeps the desktop ratio,
     so its wide banner fills the width instead of floating in empty space. */
  @media (max-width: 767px) {
    ${props => props.$hasMobile && `
      aspect-ratio: 1122 / 1402;
      max-height: 640px;
      background: ${COLORS.gray900};
    `}
  }
`;

const StyledImage = styled(Image)<{ $objectFit: 'cover' | 'contain'; $hasMobile?: boolean }>`
  object-fit: ${props => props.$objectFit};
  object-position: center center;

  @media (max-width: 767px) {
    ${props => (props.$hasMobile ? 'display: none;' : '')}
  }
`;

const MobileImage = styled(Image)`
  display: none;
  /* Always "contain" on mobile: the source images vary slightly in aspect
     ratio, and contain guarantees the baked-in text and button are never
     cropped, regardless of the exact device width. */
  object-fit: contain;
  object-position: center center;

  @media (max-width: 767px) {
    display: block;
  }
`;

const TextOverlay = styled.div<{ $position: 'left' | 'center' | 'right' }>`
  position: absolute;
  top: 50%;
  ${props => {
    switch (props.$position) {
      case 'left':
        return 'left: 5%; transform: translateY(-50%);';
      case 'right':
        return 'right: 5%; transform: translateY(-50%);';
      case 'center':
      default:
        return 'left: 50%; transform: translate(-50%, -50%);';
    }
  }}
  z-index: 5;
  text-align: ${props => props.$position};
  width: 100%;
  max-width: 1200px;
  padding: 0 20px;

  @media (max-width: 767px) {
    left: 50% !important;
    right: auto !important;
    transform: translate(-50%, -50%) !important;
    text-align: center;
    padding: 0 15px;
    max-width: 95%;
  }
`;

const TextContent = styled.div`
  padding: 20px 40px;
  margin: 0 auto;
  max-width: 800px;

  @media (max-width: 1024px) {
    padding: 16px 30px;
  }

  @media (max-width: 767px) {
    padding: 12px 20px;
  }

  @media (max-width: 479px) {
    padding: 10px 16px;
  }
`;

const Headline = styled.h1`
  font-size: 42px;
  font-weight: 700;
  color: ${COLORS.white};
  margin: 0 0 10px 0;
  line-height: 1.2;
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.8);

  @media (max-width: 1024px) {
    font-size: 32px;
    margin-bottom: 8px;
  }

  @media (max-width: 767px) {
    font-size: 24px;
    margin-bottom: 6px;
  }

  @media (max-width: 479px) {
    font-size: 18px;
    margin-bottom: 5px;
  }
`;

const Subheadline = styled.p`
  font-size: 20px;
  font-weight: 400;
  color: ${COLORS.white};
  margin: 0 0 16px 0;
  line-height: 1.4;
  text-shadow: 1px 1px 6px rgba(0, 0, 0, 0.8);

  @media (max-width: 1024px) {
    font-size: 18px;
    margin-bottom: 14px;
  }

  @media (max-width: 767px) {
    font-size: 15px;
    margin-bottom: 10px;
  }

  @media (max-width: 479px) {
    font-size: 13px;
    margin-bottom: 8px;
  }
`;

const Description = styled.p`
  font-size: 16px;
  font-weight: 400;
  color: ${COLORS.white};
  margin: 0;
  line-height: 1.5;
  max-width: 700px;
  margin: 0 auto;
  text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.8);

  @media (max-width: 1024px) {
    font-size: 14px;
    line-height: 1.5;
  }

  @media (max-width: 767px) {
    font-size: 13px;
    line-height: 1.4;
  }

  @media (max-width: 479px) {
    font-size: 11px;
    line-height: 1.4;
  }
`;
